import { useEffect, useState } from 'react';
import { callCenterApi } from './api';
import { useCallCenter } from './store';

/**
 * How many call-centre orders are waiting at THIS branch, for the sidebar
 * badge.
 *
 * A separate, deliberately cheap hook rather than a second `useLiveOrders`.
 * The badge lives in the sidebar, which is mounted for the whole session on
 * every till in the estate; giving it a full order list and an SSE connection
 * would mean every counter in the company holds an open stream all day to
 * render a number. The count refreshes on a slow timer and jumps to the exact
 * figure the moment the cashier opens the Pending tab, which is the only moment
 * precision matters.
 *
 * Silent on failure and on tenants without the module — the badge simply does
 * not appear, and neither does the nav entry.
 */
export function usePendingCount(): number {
  const bootstrap = useCallCenter((s) => s.bootstrap);
  const enabled = Boolean(bootstrap?.provisioned && bootstrap.settings.isEnabled);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return;
    }
    let alive = true;
    const run = (): void => {
      void callCenterApi.branch
        // limit: 1 — we want `total`, not the rows. The envelope carries the
        // count, so asking for a page of one is the cheapest honest answer.
        .pending({ open_only: true, limit: 1 })
        .then((r) => {
          if (alive) setCount(r.total);
        })
        .catch(() => {
          /* a badge is not worth a toast */
        });
    };
    run();
    const id = setInterval(run, 30_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [enabled]);

  return count;
}
